"use client";

import { Accordion, Modal } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useUserStore } from "../../store/user-store";
import { getUserCompletionStatus } from "../../utils/getUserCompletionStatus";

const LeftSidebar = ({ toggleActive, isSidebarActive }: { toggleActive: () => void; isSidebarActive: boolean }) => {
  const location = useLocation();
  const pathname = location.pathname;

  const [isHovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Por debajo de 1200px la barra se muestra superpuesta al contenido: al navegar se cierra.
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1199px)").matches;

  const user = useUserStore((state) => state.user);

  const completion = getUserCompletionStatus(user || {});
  const isBlocked = !completion?.summary?.isComplete;

  const labelMap: any = {
    profile: {
      province: "Provincia",
      city: "Ciudad",
      gender: "Género",
      cellphone: "Número de celular",
    },
    bank: {
      accountType: "Tipo de cuenta",
      bank: "Banco",
      accountNumber: "Número de cuenta",
      accountHolderName: "Nombre del titular",
      identificationType: "Tipo de identificación",
      identificationNumber: "Número de identificación",
    },
    address: {
      hasAddress: "Dirección registrada",
    },
    documents: {
      bankCertificate: "Certificado bancario",
      identityDocument: "Documento de identidad",
    },
  };

  const routeMap: any = {
    profile: "/my-profile",
    bank: "/settings/account-bank",
    address: "/settings/address",
    documents: "/settings/documents",
  };

  return (
    <div
      className="sidebar-area"
      onMouseEnter={!isMobile ? () => setHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => setHovered(false) : undefined}
    >
      <div className="logo position-relative">
        <Link to="/" className="d-block text-decoration-none position-relative">
          {!isSidebarActive || isHovered ? (
            <img src="/images/logo.svg" alt="logo" />
          ) : (
            <img src="/images/logo-icon.svg" alt="logo-icon" width={26} height={26} />
          )}
        </Link>

        <button
          className="sidebar-burger-menu bg-transparent p-0 border-0 opacity-0 z-n1 position-absolute top-50 end-0 translate-middle-y"
          onClick={toggleActive}
        >
          <i className="material-symbols-outlined fs-24">close</i>
        </button>
      </div>

      <div className="sidebar-menu">
        <div className="menu-title small text-uppercase">
          <span className="menu-title-text">Menú</span>
        </div>

        <Accordion defaultActiveKey="0" flush>
          <div className="menu-item">
            <Link
              to="/store"
              onClick={isMobile ? toggleActive : undefined}
              className={`menu-link ${pathname === "/store" ? "active" : ""}`}
            >
              <i className="material-symbols-outlined">storefront</i>
              <span className="title">Tienda</span>
            </Link>
          </div>

          <div className="menu-item">
            <Link
              to="/orders"
              onClick={isMobile ? toggleActive : undefined}
              className={`menu-link ${pathname === "/orders" ? "active" : ""}`}
            >
              <i className="material-symbols-outlined">receipt</i>
              <span className="title">Mis ordenes</span>
            </Link>
          </div>

          {isBlocked && (
            <div className="mt-3">
              <button
                className="btn btn-warning w-100 fw-bold"
                onClick={() => setShowModal(true)}
              >
                <i className="material-symbols-outlined me-2" style={{ fontSize: '18px', verticalAlign: 'middle' }}>verified</i>
                Convertirme en distribuidor
              </button>
            </div>
          )}

          {!isBlocked && (
            <>
              <div className="menu-item">
                <Link to="/team" onClick={isMobile ? toggleActive : undefined} className={`menu-link ${pathname === "/team" ? "active" : ""}`}>
                  <i className="material-symbols-outlined">group</i>
                  <span className="title">Mi equipo</span>
                </Link>
              </div>

              <div className="menu-item">
                <Link to="/influencer-bonus" onClick={isMobile ? toggleActive : undefined} className={`menu-link ${pathname === "/influencer-bonus" ? "active" : ""}`}>
                  <i className="material-symbols-outlined">credit_card</i>
                  <span className="title">Bonos Influencer</span>
                </Link>
              </div>

              <div className="menu-item">
                <Link to="/override-bonus" onClick={isMobile ? toggleActive : undefined} className={`menu-link ${pathname === "/override-bonus" ? "active" : ""}`}>
                  <i className="material-symbols-outlined">card_giftcard</i>
                  <span className="title">Regalías</span>
                </Link>
              </div>

              <div className="menu-item">
                <Link to="/rank" onClick={isMobile ? toggleActive : undefined} className={`menu-link ${pathname === "/rank" ? "active" : ""}`}>
                  <i className="material-symbols-outlined">star</i>
                  <span className="title">Rangos</span>
                </Link>
              </div>
            </>
          )}
        </Accordion>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">
            <i className="material-symbols-outlined me-2 text-warning" style={{ fontSize: '24px', verticalAlign: 'middle' }}>verified</i>
            Convertirme en Distribuidor
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">
          <p className="mb-3 text-white-50">
            Para ser distribuidor de Multyhealth, debes completar la siguiente información:
          </p>

          <Accordion flush alwaysOpen>
            {completion?.tasks.map((task: any, index: number) => {
              const allCompleted = Object.values(task.items).every((v: any) => v);

              return (
                <Accordion.Item 
                  eventKey={String(index)} 
                  key={task.key} 
                  className="bg-dark border-secondary"
                  style={{ backgroundColor: '#101010' }}
                >
                  <Accordion.Header 
                    className="bg-dark"
                    style={{ backgroundColor: '#101010' }}
                  >
                    <div className="d-flex align-items-center w-100 gap-2" style={{ backgroundColor: '#101010' }}>
                      <span className="flex-shrink-0">
                        {allCompleted ? (
                          <i className="ri-checkbox-circle-fill text-success fs-5"></i>
                        ) : (
                          <i className="ri-time-line text-warning fs-5"></i>
                        )}
                      </span>

                      <span className="fw-semibold flex-grow-1 text-white">
                        {task.label}
                      </span>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body className="bg-dark text-white">
                    <ul className="ps-3 mb-0">
                      {Object.entries(task.items).map(([key, value]: any) => (
                        <li key={key} className="mb-2">
                          <Link
                            to={routeMap[task.key]}
                            className="d-flex justify-content-between align-items-center text-decoration-none text-white"
                            style={{ opacity: value ? 1 : 0.7 }}
                            onClick={() => setShowModal(false)}
                          >
                            <span>
                              {labelMap[task.key]?.[key] || key}
                            </span>

                            {value ? (
                              <span className="text-success fw-bold">✔</span>
                            ) : (
                              <span className="text-danger fw-bold">✖</span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeftSidebar;